import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  getClassDisplayName,
  getSchoolByIdFromRecords,
  parseTermFilter,
  useGetAvgAnalyticsStudentQuery,
  useGetRegionalHolisticAnalyticsQuery,
} from '../../modules/student-holistic/services/holisticApi';

const breadcrumbNameMap: Record<string, string> = {
  '/dashboard': 'Dashboard',

  // KVS Samagam Module
  '/schools': 'Schools',
  '/school': 'School Details',
  '/analytics': 'School Analytics',
  '/analytics/enrollment': 'Enrollment Analysis',
  '/analytics/infrastructure': 'Infrastructure Analysis',
  '/analytics/ict-profile': 'ICT Profile Analysis',
  '/analytics/library-profile': 'Library Profile Analysis',
  '/analytics/safety': 'Safety Analysis',
  '/analytics/sports': 'Sports Infrastructure Analysis',
  '/analytics/ncc-profile': 'NCC Profile Analysis',
  '/analytics/special-labs': 'Special Labs Analysis',
  '/analytics/cwsn-profile': 'CWSN Profile Analysis',
  '/analytics/green-building': 'Green Building Analysis',

  // Student Holistic Analytics
  '/student-analytics/stages': 'Stage Selection',
  '/student-analytics/stages/foundation': 'Foundational Stage',
  '/student-analytics/stages/preparatory': 'Preparatory Stage',

  // Middle Stage (Classes VI–VIII)
  '/student-analytics/stages/middle': 'Middle Stage',
  '/student-analytics/stages/middle/school-performance': 'School Performance',
  '/student-analytics/stages/middle/students': 'All Students',
  '/student-analytics/stages/middle/academic': 'Academic Evaluation',

  // Secondary Stage (Classes IX, XI)
  '/student-analytics/stages/secondary': 'Secondary Stage',
  '/student-analytics/stages/secondary/school-performance': 'School Performance',
  '/student-analytics/stages/secondary/students': 'All Students',
  '/student-analytics/stages/secondary/academic': 'Academic Evaluation',

  '/student-analytics/students': 'All Students',
  '/student-analytics/behavior': 'Behavior Analysis',
  '/student-analytics/peer': 'Peer Analysis',
  '/student-analytics/parent': 'Parent Feedback',
  '/student-analytics/teacher': 'Teacher Observations',
  '/student-analytics/physical': 'Physical Development',
  '/student-analytics/interests': 'Interests & Lifestyle',
  '/student-analytics/reports': 'Reports',
  
  
  // PM-SHRI Module
  '/pm-shri': 'PM SHRI',
  '/pm-shri/activity-tree': 'Activity Tree',
  '/pm-shri/expense-activity-tree': 'Expense Activity Tree',
  '/pm-shri/expenditure-activity-tree': 'Expenditure Activity Tree',
  '/pm-shri/analytics/heads': 'Head Analytics',
  '/pm-shri/analytics/heads/subheads': 'Subhead Analytics',
  '/pm-shri/analytics/heads/subheads/activities': 'Activity Analytics',
  '/pm-shri/analytics/regions': 'Region Analytics',
  '/pm-shri/schools': 'School Details',
  
  '/diksha': 'DIKSHA',
  '/nishtha': 'NISHTHA',
  '/udise': 'UDISE+',
  
  // Attendance Module
  '/attendance': 'Attendance',
  '/attendance/employee': 'Employee Attendance',
  '/attendance/employee/register': 'Employee Attendance Register',
  '/attendance/student/register': 'Student Attendance Register',
  
  // Employee Analytics
  '/attendance/analytics': 'Employee Analytics',
  '/attendance/analytics/compare': 'Employee Trends & Comparison',
  '/attendance/analytics/kv': 'KV Details',
  '/attendance/analytics/kv/employee': 'Employee Details',
  
  // Student Analytics
  '/attendance/student/analytics/compare': 'Student Trends & Comparison',
  '/attendance/student/analytics/kv': 'KV Details',
  
  '/support': 'Support',
  // Legacy paths for backward compatibility
  '/Pm_shri/PMShriPage': 'PM Shri',
  '/Diksha/DikshaPage': 'DIKSHA',
  '/Nishtha/NishthaPage': 'NISHTHA',
  '/Udise/UDISEPage': 'UDISE+',
  '/Attendance/AttendancePage': 'Attendance',

  // Result Module
  '/result': 'Result',

  // Admission Module
  '/admission': 'Admission',
  '/admission/geographic': 'Geographic Analysis',
  '/admission/rte': 'RTE Analysis',
  '/admission/distance': 'Distance Analysis',
};

export default function Breadcrumbs() {
  const location = useLocation();

  // Keep labels independent from the /kvs-samagam segment.
  const resolveBreadcrumbLabel = (path: string) => {
    if (breadcrumbNameMap[path]) return breadcrumbNameMap[path];
    const normalizedPath = path.startsWith('/kvs-samagam/')
      ? path.replace('/kvs-samagam', '')
      : path;
    return breadcrumbNameMap[normalizedPath];
  };
  
  // Don't show breadcrumb on home/landing page or login page
  const shouldHideBreadcrumb = location.pathname === '/' || location.pathname === '/login';
  
  const pathnames = location.pathname.split('/').filter((x) => x);
  const searchParams = new URLSearchParams(location.search);
  
  // Determine if we're in student flow
  const isStudentFlow = location.pathname.includes('/student');
  
  // Check if we're on a direct analytics page (not coming through intermediate pages)
  const isDirectAnalytics = location.pathname === '/attendance/student/analytics/compare' || 
                           location.pathname === '/attendance/analytics/compare';

  // Build all possible accumulated paths
  let accumulatedPath = '';
  const breadcrumbItems: { to: string; label: string }[] = [];

  // Student analytics drill-down: label dynamic segments by their preceding keyword
  const isHolisticPath =
    location.pathname.startsWith('/kvs-samagam/student-analytics') ||
    location.pathname.startsWith('/student-analytics');
  const isResultPath =
    location.pathname.startsWith('/kvs-samagam/result') ||
    location.pathname.startsWith('/result');
  const isAdmissionPath = location.pathname.startsWith('/admission');

  // Detect specific stage sub-paths for middle and secondary
  const normalizedPathnameForStage = location.pathname.startsWith('/kvs-samagam/')
    ? location.pathname.replace('/kvs-samagam', '')
    : location.pathname;
  const isMiddleStagePath = normalizedPathnameForStage.startsWith('/student-analytics/stages/middle');
  const isSecondaryStagePath = normalizedPathnameForStage.startsWith('/student-analytics/stages/secondary');
  const isStageDrillPath = isMiddleStagePath || isSecondaryStagePath;

  const getHolisticParamValue = (keyword: 'region' | 'school' | 'class' | 'students' | 'student') => {
    const idx = pathnames.findIndex((segment) => segment === keyword);
    if (idx < 0 || idx + 1 >= pathnames.length) return undefined;
    return decodeURIComponent(pathnames[idx + 1]);
  };

  const regionIdParam = getHolisticParamValue('region');
  const schoolIdParam = getHolisticParamValue('school');
  const studentIdParam = getHolisticParamValue('students') || getHolisticParamValue('student');
  const academicYear = Number(searchParams.get('year') || '2025');
  const parsedTerm = parseTermFilter(searchParams.get('term'));
  const term = parsedTerm === 'overall' ? 1 : parsedTerm;

  const regionalHolisticQuery = useGetRegionalHolisticAnalyticsQuery(
    isHolisticPath && regionIdParam
      ? {
          regionId: Number(regionIdParam),
          academicYear,
          term,
        }
      : skipToken,
  );

  const studentHolisticQuery = useGetAvgAnalyticsStudentQuery(
    isHolisticPath && studentIdParam
      ? {
          studentId: String(studentIdParam),
          academicYear,
          term,
        }
      : skipToken,
    { skip: !(isHolisticPath && studentIdParam) },
  );

  const resolvedRegionName =
    regionalHolisticQuery.data?.data?.[0]?.roName ||
    searchParams.get('regionName') ||
    undefined;

  const resolvedSchoolName = schoolIdParam
    ? getSchoolByIdFromRecords(regionalHolisticQuery.data?.data || [], schoolIdParam)?.schoolName ||
      searchParams.get('schoolName') ||
      undefined
    : undefined;

  const resolvedStudentName = (() => {
    const studentData = studentHolisticQuery.data;
    if (studentData) {
      const fullName = [studentData.firstName, studentData.middleName, studentData.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
      if (fullName) return fullName;
    }
    return searchParams.get('studentName') || undefined;
  })();
  const holisticKeywordMap: Record<string, string> = {
    region: 'Region',
    school: 'School',
    class: 'Class',
    students: 'Student',
    student: 'Student',
  };

  // Keyword map for middle/secondary stage drill-down (region → school → class → student)
  const stageKeywordMap: Record<string, string> = {
    region: 'Region',
    school: 'School',
    class: 'Class',
    students: 'Student',
    student: 'Student',
  };

  const CLASS_NAMES_MIDDLE: Record<string, string> = { '6': 'Class VI', '7': 'Class VII', '8': 'Class VIII' };
  const CLASS_NAMES_SECONDARY: Record<string, string> = { '9': 'Class IX', '11': 'Class XI' };

  const resolveStageClassLabel = (classId: string): string => {
    return CLASS_NAMES_MIDDLE[classId] || CLASS_NAMES_SECONDARY[classId] || `Class ${classId}`;
  };

  const resultKeywordMap: Record<string, string> = {
    region: 'Region',
    school: 'School',
    student: 'Student',
  };

  const resolveResultSegmentValue = (keyword: 'region' | 'school' | 'student', rawValue: string) => {
    const decodedValue = decodeURIComponent(rawValue);

    if (keyword === 'region') {
      return searchParams.get('regionName') || decodedValue;
    }

    if (keyword === 'school') {
      const schoolName = searchParams.get('schoolName');
      return schoolName || `KV Code ${decodedValue}`;
    }

    if (keyword === 'student') {
      const studentName = searchParams.get('studentName');
      return studentName || `Roll No ${decodedValue}`;
    }

    return decodedValue;
  };

  // Admission geographic drill-down: /admission/geographic/region|kv/:value
  const admissionKeywordMap: Record<string, string> = {
    region: 'Region',
    kv: 'KV',
  };

  let lastHolisticKeyword = '';
  let lastResultKeyword = '';
  let lastStageKeyword = '';
  let lastAdmissionKeyword = '';

  for (let i = 0; i < pathnames.length; i++) {
    accumulatedPath += '/' + pathnames[i];
    
    // Skip intermediate /attendance/student path when navigating directly to analytics
    if (isDirectAnalytics && isStudentFlow && accumulatedPath === '/attendance/student') {
      continue;
    }
    
    // Check if this exact path has a mapping
    const directLabel = resolveBreadcrumbLabel(accumulatedPath);
    if (directLabel) {
      let breadcrumbTo = accumulatedPath;
      
      // Context-aware link: If we're in student flow and the breadcrumb is "Attendance",
      // link to student attendance dashboard (with role=student query param)
      if (accumulatedPath === '/attendance' && isStudentFlow) {
        breadcrumbTo = '/attendance?role=student';
      }
      
      const previous = breadcrumbItems[breadcrumbItems.length - 1];
      if (!previous || previous.label !== directLabel || previous.to !== breadcrumbTo) {
        breadcrumbItems.push({ to: breadcrumbTo, label: directLabel });
      }
    } else {
      // Student analytics: handle 'list' filter paths
      if (isHolisticPath && pathnames[i] === 'list') {
        // Skip: no breadcrumb for 'list' itself
      } else if (isHolisticPath && i > 0 && pathnames[i - 1] === 'list') {
        const listLabels: Record<string, string> = { regions: 'All Regions', schools: 'All Schools', classes: 'All Classes' };
        if (listLabels[pathnames[i]]) {
          breadcrumbItems.push({ to: accumulatedPath, label: listLabels[pathnames[i]] });
        }
      } else if (isHolisticPath && i > 1 && pathnames[i - 2] === 'list') {
        const filterLabels: Record<string, string> = { excellent: 'Excellent', good: 'Good', average: 'Average', 'below-average': 'Below Average' };
        if (filterLabels[pathnames[i]]) {
          breadcrumbItems.push({ to: accumulatedPath, label: filterLabels[pathnames[i]] });
        }
      }
      // Middle/Secondary stage drill-down: keyword-based labels (region → school → class → student)
      else if (isStageDrillPath && lastStageKeyword && stageKeywordMap[lastStageKeyword]) {
        const rawValue = decodeURIComponent(pathnames[i]);
        let displayValue = rawValue;

        if (lastStageKeyword === 'region') {
          displayValue = searchParams.get('regionName') || rawValue;
        } else if (lastStageKeyword === 'school') {
          displayValue = searchParams.get('schoolName') || rawValue;
        } else if (lastStageKeyword === 'class') {
          displayValue = resolveStageClassLabel(rawValue);
        } else if (lastStageKeyword === 'students' || lastStageKeyword === 'student') {
          displayValue = searchParams.get('studentName') || rawValue;
        }

        const label = `${stageKeywordMap[lastStageKeyword]}: ${displayValue}`;
        breadcrumbItems.push({ to: accumulatedPath, label });
        lastStageKeyword = '';
      }
      // Foundation/Preparatory stage drill-down: use keyword-based labels for dynamic IDs
      else if (isHolisticPath && !isStageDrillPath && lastHolisticKeyword && holisticKeywordMap[lastHolisticKeyword]) {
        const rawValue = decodeURIComponent(pathnames[i]);
        let displayValue = rawValue;

        if (lastHolisticKeyword === 'region') {
          displayValue = resolvedRegionName || rawValue;
        } else if (lastHolisticKeyword === 'school') {
          displayValue = resolvedSchoolName || rawValue;
        } else if (lastHolisticKeyword === 'class') {
          const classId = Number(rawValue);
          displayValue = Number.isFinite(classId) ? getClassDisplayName(classId) : rawValue;
        } else if (lastHolisticKeyword === 'students' || lastHolisticKeyword === 'student') {
          displayValue = resolvedStudentName || rawValue;
        }

        const label = `${holisticKeywordMap[lastHolisticKeyword]}: ${displayValue}`;
        breadcrumbItems.push({ to: accumulatedPath, label });
        lastHolisticKeyword = '';
      } else if (isResultPath && lastResultKeyword && resultKeywordMap[lastResultKeyword]) {
        const displayValue = resolveResultSegmentValue(
          lastResultKeyword as 'region' | 'school' | 'student',
          pathnames[i],
        );
        const label = `${resultKeywordMap[lastResultKeyword]}: ${displayValue}`;
        breadcrumbItems.push({ to: accumulatedPath, label });
        lastResultKeyword = '';
      } else if (isAdmissionPath && lastAdmissionKeyword && admissionKeywordMap[lastAdmissionKeyword]) {
        const displayValue = decodeURIComponent(pathnames[i]);
        const label = `${admissionKeywordMap[lastAdmissionKeyword]}: ${displayValue}`;
        breadcrumbItems.push({ to: accumulatedPath, label });
        lastAdmissionKeyword = '';
      } else {
        // For dynamic routes like /kvs-samagam/school/:schoolID
        // Try to find a pattern match
        const parts = accumulatedPath.split('/').filter(x => x);
        if (parts.length >= 2) {
          const patternPath = '/' + parts.slice(0, -1).join('/') + '/' + pathnames[i];
          const patternLabel = resolveBreadcrumbLabel(patternPath);
          if (patternLabel) {
            breadcrumbItems.push({ to: accumulatedPath, label: patternLabel });
          }
        }
      }
    }

    // Track keyword for next segment
    if (isStageDrillPath && stageKeywordMap[pathnames[i]]) {
      lastStageKeyword = pathnames[i];
    } else if (isHolisticPath && !isStageDrillPath && holisticKeywordMap[pathnames[i]]) {
      lastHolisticKeyword = pathnames[i];
    }

    if (isResultPath && resultKeywordMap[pathnames[i]]) {
      lastResultKeyword = pathnames[i];
    }

    if (isAdmissionPath && admissionKeywordMap[pathnames[i]]) {
      lastAdmissionKeyword = pathnames[i];
    }
  }

  const normalizedPath = location.pathname.startsWith('/kvs-samagam/')
    ? location.pathname.replace('/kvs-samagam', '')
    : location.pathname;
  const isPrefixedHolistic = location.pathname.startsWith('/kvs-samagam/student-analytics');
  const stageRootPath = isPrefixedHolistic
    ? '/kvs-samagam/student-analytics/stages'
    : '/student-analytics/stages';
  const studentAnalyticsPath = isPrefixedHolistic
    ? '/kvs-samagam/student-analytics'
    : '/student-analytics';
  // Foundation: any /student-analytics path that is NOT under /stages
  const isFoundationScopedPath =
    normalizedPath.startsWith('/student-analytics') &&
    !normalizedPath.startsWith('/student-analytics/stages');

  const foundationDashboardPath = isPrefixedHolistic
    ? '/kvs-samagam/student-analytics'
    : '/student-analytics';

  // Override breadcrumb for all stage-related paths.
  if (normalizedPath === '/student-analytics/stages') {
    breadcrumbItems.length = 0;
    breadcrumbItems.push(
      { to: stageRootPath, label: 'Stage Selection' },
    );
  } else if (normalizedPath === '/student-analytics/stages/preparatory') {
    breadcrumbItems.length = 0;
    breadcrumbItems.push(
      { to: stageRootPath, label: 'Stage Selection' },
      { to: location.pathname, label: 'Preparatory Stage' },
    );
  } else if (isMiddleStagePath) {
    // Middle stage: Stage Selection → Middle Stage → [drill-down items]
    const drillDownItems = breadcrumbItems.filter(
      (item) => /^(Region|School|Class|Student):/.test(item.label) || /^All /.test(item.label),
    );
    const middleDashboardPath = isPrefixedHolistic
      ? '/kvs-samagam/student-analytics/stages/middle'
      : '/student-analytics/stages/middle';
    breadcrumbItems.length = 0;
    breadcrumbItems.push({ to: stageRootPath, label: 'Stage Selection' });
    breadcrumbItems.push({ to: middleDashboardPath, label: 'Middle Stage' });
    drillDownItems.forEach((item) => breadcrumbItems.push(item));
  } else if (isSecondaryStagePath) {
    // Secondary stage: Stage Selection → Secondary Stage → [drill-down items]
    const drillDownItems = breadcrumbItems.filter(
      (item) => /^(Region|School|Class|Student):/.test(item.label) || /^All /.test(item.label),
    );
    const secondaryDashboardPath = isPrefixedHolistic
      ? '/kvs-samagam/student-analytics/stages/secondary'
      : '/student-analytics/stages/secondary';
    breadcrumbItems.length = 0;
    breadcrumbItems.push({ to: stageRootPath, label: 'Stage Selection' });
    breadcrumbItems.push({ to: secondaryDashboardPath, label: 'Secondary Stage' });
    drillDownItems.forEach((item) => breadcrumbItems.push(item));
  } else if (isFoundationScopedPath) {
    // Keep drill-down items built by the loop (Region:, School:, Class:, Student:, All …)
    const drillDownItems = breadcrumbItems.filter((item) =>
      /^(Region|School|Class|Student):/.test(item.label) || /^All /.test(item.label),
    );
    const isOnFoundationRoot = normalizedPath === '/student-analytics';

    breadcrumbItems.length = 0;
    breadcrumbItems.push({ to: stageRootPath, label: 'Stage Selection' });
    breadcrumbItems.push({ to: foundationDashboardPath, label: 'Foundational Stage' });

    if (!isOnFoundationRoot) {
      drillDownItems.forEach((item) => breadcrumbItems.push(item));
    }
  }

  // Don't show breadcrumb if we're on dashboard (home)
  if (shouldHideBreadcrumb) {
    return null;
  }

  // Don't show breadcrumb if we're on dashboard (home)
  if (location.pathname === '/dashboard' && breadcrumbItems.length === 0) {
    return null;
  }

  return (
    <nav className="text-sm text-gray-500 mb-4 px-10 pt-4" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        <li>
          <Link to="/dashboard" className="hover:underline text-grey font-semibold">
            🏠 Home
          </Link>
        </li>
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          return (
            <li key={item.to} className="flex items-center">
              <span className="mx-2">/</span>
              {isLast ? (
                <span className="text-[#6A1786] font-bold">{item.label}</span>
              ) : (
                <Link to={item.to} className="hover:underline text-[#6A1786]">{item.label}</Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
